import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * How long the drawer waits for a fresh cart render after a Rebuy add before opening anyway.
 * Measured render time on the preview theme: ~400ms. Generous enough for a slow connection, short
 * enough that the cart is never held hostage to a stalled request. See `#handleRebuyCartAdd`.
 * @type {number}
 */
const DRAWER_REFRESH_BUDGET = 900;

/**
 * How many times to ask for a fresh cart section before giving up. The drawer is already open by
 * then, so these cost the shopper nothing but a late correction.
 * @type {number}
 */
const DRAWER_REFRESH_ATTEMPTS = 3;

/** @type {number} Base backoff between refresh attempts, multiplied by the attempt number. */
const DRAWER_REFRESH_RETRY = 600;

/**
 * @param {Event} event A `rebuy.add`.
 * @returns {boolean} Whether a Rebuy popup belonging to a DIFFERENT widget is on screen.
 *
 * The case this exists for, measured on the AirMini PDP: the shopper's first add brings up the
 * bundle popup (widget 289411), and ~3s later the gift-with-purchase widget (199918) adds the
 * Masterclass on its own. Its `rebuy.add` opened the drawer UNDER the popup, with `body` pinned
 * `position: fixed` by the drawer's scroll lock. That first popup would not scroll on iOS Safari
 * ("skip" out of reach); reopened, with no drawer behind it, it did (reported 2026-09-24). Only the
 * first add does it, because the gift is only added once.
 *
 * The cart still refreshes; only the opening waits. Whoever owns the popup opens the drawer when it
 * is answered — for the bundle, assets/sl-bundle-upsell-close.js on `rebuy.hide` / `rebuy.add`. A
 * popup's OWN add is never blocked (same widget id, and that popup is hidden by the time this runs).
 */
function isUnderAnotherRebuyPopup(event) {
  const popup = document.querySelector('.rebuy-widget-container.widget-display-popup.is-visible');
  if (!popup) return false;

  const id = /** @type {any} */ (event).detail?.widget?.id;
  return popup.closest('.rebuy-widget')?.id !== `rebuy-widget-${id}`;
}

/**
 * @returns {boolean} Whether an RX modal owns the screen: the legacy `#rx-modal` or the prescription
 * popup is open, or the prescription popup is about to open for the add in flight
 * (`data-rx-modal-pending`, assets/product-form.js). It owns what follows that add, the cart drawer
 * included (#handleRxModalClose).
 */
function rxModalOwnsScreen() {
  return (
    document.body.hasAttribute('data-rx-modal-pending') ||
    Boolean(document.querySelector(':is(#rx-modal, [data-rx-modal]) dialog[open]'))
  );
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('rebuy.add', this.#handleRebuyCartAdd);
    document.addEventListener('submit', this.#handleAddToCartSubmit, { capture: true });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#handleOpenFromUrl();
    // The rx-modal does not exist at page load on collection pages — it arrives
    // injected by the quick-add flow — so a listener attached to it here would
    // never fire. dialog:close does not bubble: delegate on document in the
    // capture phase, which reaches the target regardless of when it was added.
    document.addEventListener(DialogCloseEvent.eventName, this.#handleRxModalClose, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('rebuy.add', this.#handleRebuyCartAdd);
    document.removeEventListener('submit', this.#handleAddToCartSubmit, { capture: true });
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    document.removeEventListener(DialogCloseEvent.eventName, this.#handleRxModalClose, { capture: true });
  }

  #handleCartAdd = (event) => {
    // `CartAddEvent.eventName` is `cart:update`, so this hears every cart update. A `background` one
    // corrects the cart on its own (a code applied after load, a page served from the Back/Forward
    // cache — assets/discount-keeper.js, assets/custom-cart-icon.js) and must not open the drawer.
    if (event.detail.data.background) return;
    if (event.detail.data.upsellShown) return;
    // An add made from inside an RX modal: the modal opens the drawer when it closes
    // (#handleRxModalClose), and after a failed add it must stay on top with its error showing.
    if (event.detail.data.fromRxModal) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Opens the drawer as soon as an add-to-cart form is submitted instead of
   * waiting the ~1.5s of network (add + sections render) before CartAddEvent —
   * the current cart shows immediately and the new item morphs in when the
   * response lands. Skipped for RX upsell forms (the modal-vs-drawer decision
   * needs the cart response) and for forms inside an open dialog (quick-add
   * popup / RX modal), which keep the existing post-response behavior.
   * @param {Event} event
   */
  #handleAddToCartSubmit = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    const component = form.closest('product-form-component');
    if (!component) return;
    // A variant sold without a prescription adds like any other (assets/product-form.js → rxExempt).
    if (component.hasAttribute('data-rxupsell') && !(/** @type {any} */ (component).rxExempt)) return;
    if (form.closest('dialog')) return;
    this.showDialog();
  };

  /**
   * A failed add leaves the cart unchanged — close the optimistically opened
   * drawer so the error message next to the buy button stays visible.
   */
  #handleCartError = () => {
    this.closeDialog();
  };

  /**
   * @param {Event} event
   */
  #handleRxModalClose = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('#rx-modal, [data-rx-modal]')) return;
    // A Rebuy popup that fired for the same add (the bundle offer on AirMini and Micro) is next in
    // line: it was held back while the RX modal was up, and assets/sl-bundle-upsell-close.js opens
    // the drawer once the shopper answers it. Opening now would lay the drawer's overlay over it.
    if (document.querySelector('.rebuy-widget-container.widget-display-popup.is-visible')) return;
    this.open();
  };

  /**
   * Opens the drawer on the cart the shopper actually has, not the one they had.
   *
   * This used to open first and re-render afterwards. For an ordinary Rebuy add that is only a
   * flicker, but the bundle upsell REPLACES a line: it removes the machine and adds the bundle, so
   * the drawer's existing DOM still lists the machine the shopper just converted. Measured on the
   * preview theme: the drawer opened at +1.68s showing "ResMed AirMini AutoSet" alongside the
   * bundle, and only corrected itself 421ms later when the section render landed. That window is
   * indistinguishable from the swap having failed — and it stretches with the connection, which is
   * exactly when a shopper is most likely to give up on it.
   *
   * So: render, then open. Bounded by DRAWER_REFRESH_BUDGET, because a render that stalls must
   * never keep the shopper from their cart — past the budget the drawer opens on what it has and
   * the morph catches up, which is the old behaviour and still better than no cart at all.
   *
   * @param {CustomEvent} event
   */
  #handleRebuyCartAdd = (event) => {
    const rendered = this.#refreshCartSection();
    const budget = new Promise((resolve) => setTimeout(resolve, DRAWER_REFRESH_BUDGET));

    // Open on whichever comes first, but keep refreshing in the background either way: the drawer
    // showing up promptly and the drawer telling the truth are two different promises.
    Promise.race([rendered, budget]).then(() => {
      requestAnimationFrame(() => {
        if (isUnderAnotherRebuyPopup(event)) return;
        // Rebuy's gift with purchase lands while the RX modal is still answering the machine's add.
        // Opening now would put the drawer behind the modal and pull focus out of it; the cart is
        // refreshed either way, and the modal opens the drawer when it closes.
        if (rxModalOwnsScreen()) return;
        this.open();
      });
    });
  };

  /**
   * Re-renders the cart section, retrying a failed attempt.
   *
   * A single attempt is not enough. Recorded on the preview during a real bundle swap: the cart
   * writes both answered 200, the cart was correct — and the refresh came back 429, so the drawer
   * opened on the DOM it already had, still listing the machine that had just been converted, and
   * nothing ever corrected it. To the shopper that is indistinguishable from the swap failing, and
   * it is exactly the "worse when the connection is bad" part of the report.
   *
   * @returns {Promise<boolean>} Whether the drawer ended up showing a freshly rendered cart.
   */
  async #refreshCartSection() {
    for (let attempt = 0; attempt < DRAWER_REFRESH_ATTEMPTS; attempt += 1) {
      if (attempt) await new Promise((resolve) => setTimeout(resolve, DRAWER_REFRESH_RETRY * attempt));

      try {
        await sectionRenderer.renderSection('cart-drawer-custom', { cache: false });
        return true;
      } catch {
        // Keep trying: the drawer is on screen showing a cart that is out of date.
      }
    }

    // Out of attempts. Say so — a drawer that disagrees with the cart is how a shopper decides the
    // add did not work, and silence here is what made that so hard to find.
    console.error('Cart drawer could not be refreshed after a Rebuy add; it may be showing a stale cart.');
    return false;
  }

  /**
   * Handle opening cart via URL param (?openCart=1)
   */
  #handleOpenFromUrl() {
    let chatBtnNew = document.querySelector(".js-chat-btn");
    if (chatBtnNew) {
      chatBtnNew.addEventListener("click", function (event) {
        try {
          event.preventDefault();
          if (window.GorgiasChat) {
            window.GorgiasChat.open()
          }
        } catch (error) {
          console.error('CartDrawerComponent chat button click handler failed:', error);
        }
      })
    }
    const params = new URLSearchParams(window.location.search);

    if (
      params.get('openCart') === '1' &&
      this === document.querySelector('cart-drawer-component')
    ) {
      customElements.whenDefined('cart-drawer-component').then(() => {
        requestAnimationFrame(() => {
          this.open();

          // Clean URL so it doesn't reopen on refresh
          history.replaceState({}, '', window.location.pathname);
        });
      });
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
